import React, { useState } from 'react';
import { 
  Trash2, 
  GripVertical, 
  Mic, 
  MicOff, 
  ChevronDown, 
  ChevronRight,
  Clock,
  MessageSquare,
  Image as ImageIcon
} from 'lucide-react';
import { InspectionFrame } from '../../types';

interface FramePanelProps {
  frames: InspectionFrame[];
  selectedFrameId: string | null;
  onFrameSelect: (frameId: string) => void;
  onFrameDelete: (frameId: string) => void;
  onFrameReorder: (frameIds: string[]) => void;
  onMarkerNoteChange: (frameId: string, markerId: string, note: string) => void;
}

export const FramePanel: React.FC<FramePanelProps> = ({
  frames,
  selectedFrameId,
  onFrameSelect,
  onFrameDelete,
  onFrameReorder,
  onMarkerNoteChange
}) => {
  const [expandedFrames, setExpandedFrames] = useState<Set<string>>(new Set());
  const [draggedFrameId, setDraggedFrameId] = useState<string | null>(null);
  const [dragOverFrameId, setDragOverFrameId] = useState<string | null>(null);

  // Format timestamp to MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle frame expansion
  const toggleExpanded = (frameId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedFrames);
    if (newExpanded.has(frameId)) {
      newExpanded.delete(frameId);
    } else {
      newExpanded.add(frameId);
    }
    setExpandedFrames(newExpanded);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, frameId: string) => {
    setDraggedFrameId(frameId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, frameId: string) => {
    e.preventDefault();
    if (frameId !== draggedFrameId) {
      setDragOverFrameId(frameId);
    }
  };

  const handleDragLeave = () => {
    setDragOverFrameId(null);
  };

  const handleDrop = (e: React.DragEvent, targetFrameId: string) => {
    e.preventDefault();
    setDragOverFrameId(null);

    if (!draggedFrameId || draggedFrameId === targetFrameId) {
      setDraggedFrameId(null);
      return;
    }

    // Reorder frames
    const currentIds = frames.map(f => f.id);
    const draggedIndex = currentIds.indexOf(draggedFrameId);
    const targetIndex = currentIds.indexOf(targetFrameId);

    const newIds = [...currentIds];
    newIds.splice(draggedIndex, 1);
    newIds.splice(targetIndex, 0, draggedFrameId);

    onFrameReorder(newIds);
    setDraggedFrameId(null);
  };

  const handleDragEnd = () => {
    setDraggedFrameId(null);
    setDragOverFrameId(null);
  };

  // Delete frame with confirmation
  const handleDelete = (frameId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Delete this frame? This action cannot be undone.')) {
      onFrameDelete(frameId);
    }
  };

  if (frames.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Frames Yet</h3>
        <p className="text-gray-500 text-sm">
          Pause the video and click "Capture" to add frames to your inspection report.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <h3 className="font-semibold text-gray-900">Frames ({frames.length})</h3>
        <p className="text-xs text-gray-500 mt-1">Drag to reorder • Click to edit</p>
      </div>

      {/* Frame List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {frames.map((frame, index) => {
          const isSelected = frame.id === selectedFrameId;
          const isExpanded = expandedFrames.has(frame.id);
          const isDragging = frame.id === draggedFrameId;
          const isDragOver = frame.id === dragOverFrameId;

          return (
            <div
              key={frame.id}
              draggable
              onDragStart={(e) => handleDragStart(e, frame.id)}
              onDragOver={(e) => handleDragOver(e, frame.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, frame.id)}
              onDragEnd={handleDragEnd}
              onClick={() => onFrameSelect(frame.id)}
              className={`
                rounded-lg border-2 transition-all cursor-pointer
                ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}
                ${isDragging ? 'opacity-50' : ''}
                ${isDragOver ? 'border-orange-400 border-dashed' : ''}
              `}
            >
              {/* Frame Header */}
              <div className="flex items-center p-2">
                {/* Drag Handle */}
                <div className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-4 h-4" />
                </div>

                {/* Thumbnail */}
                <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0 ml-1">
                  <img
                    src={frame.markupUrl || frame.screenshotUrl}
                    alt={`Frame ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Frame Info */}
                <div className="flex-1 ml-3 min-w-0">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">Frame {index + 1}</span>
                    {frame.audioUrl && (
                      <Mic className="w-3 h-3 text-green-500 ml-2" />
                    )}
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatTime(frame.timestamp)}
                    {frame.markers.length > 0 && (
                      <span className="ml-2 flex items-center">
                        <MessageSquare className="w-3 h-3 mr-1" />
                        {frame.markers.length}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-1">
                  {/* Expand/Collapse */}
                  {frame.markers.length > 0 && (
                    <button
                      onClick={(e) => toggleExpanded(frame.id, e)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  
                  {/* Delete */}
                  <button
                    onClick={(e) => handleDelete(frame.id, e)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Markers Section */}
              {isExpanded && frame.markers.length > 0 && (
                <div className="px-2 pb-2 space-y-2">
                  <div className="border-t border-gray-100 pt-2">
                    {frame.markers.map((marker, markerIndex) => (
                      <div key={marker.id} className="flex items-start space-x-2 py-1">
                        <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {markerIndex + 1}
                        </div>
                        <textarea
                          value={marker.note}
                          onChange={(e) => onMarkerNoteChange(frame.id, marker.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Add note for this marker..."
                          className="flex-1 text-sm border border-gray-200 rounded p-1.5 resize-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audio Status */}
              {frame.audioUrl && (
                <div className="px-2 pb-2">
                  <div className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                    <Mic className="w-3 h-3 mr-1" />
                    Audio recorded
                    {frame.audioTranscript && (
                      <span className="ml-2 text-gray-500">• Transcribed</span>
                    )}
                  </div>
                </div>
              )}

              {/* No Audio Indicator */}
              {!frame.audioUrl && isSelected && (
                <div className="px-2 pb-2">
                  <div className="flex items-center text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">
                    <MicOff className="w-3 h-3 mr-1" />
                    No audio recorded
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {frames.filter(f => f.audioUrl).length}/{frames.length} with audio
          </span>
          <span>
            {frames.reduce((acc, f) => acc + f.markers.length, 0)} total markers
          </span>
        </div>
      </div>
    </div>
  );
};
